import type { ReactNode } from 'react';
import { cx, TONE_WORD } from '../internal/tone';

export interface ListProps {
  children: ReactNode;
  /** Nome da lista para o leitor de tela, ex. "Medicamentos de hoje". */
  label?: string;
  className?: string;
}

/** Lista. É <ul>/<li> a sério para o leitor de tela anunciar "1 de 7". */
export function List({ children, label, className }: ListProps) {
  return (
    <ul className={cx('flos-list', className)} aria-label={label}>
      {children}
    </ul>
  );
}

export interface ListItemProps {
  title: string;
  subtitle?: string;
  icon?: string;
  /**
   * Ações da linha — tipicamente um `BigButton`.
   *
   * Fica FORA do botão da linha, e isso não é um pormenor de marcação: um botão
   * dentro de outro botão é HTML inválido, e na prática um toque no de dentro
   * dispara os dois. Numa lista de compras isso marca o item como comprado duas
   * vezes; numa lista de tomas, registaria duas doses.
   */
  trailing?: ReactNode;
  onClick?: () => void;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
  className?: string;
}

export function ListItem({
  title,
  subtitle,
  icon,
  trailing,
  onClick,
  tone = 'neutral',
  className,
}: ListItemProps) {
  const body = (
    <>
      {tone !== 'neutral' ? <span className="flos-sr-only">{TONE_WORD[tone]}. </span> : null}
      {icon ? (
        <span className="flos-list-item__icon flos-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="flos-list-item__text">
        <span className="flos-list-item__title">{title}</span>
        {subtitle ? <span className="flos-list-item__subtitle">{subtitle}</span> : null}
      </span>
    </>
  );

  return (
    <li
      className={cx('flos-list-item', tone !== 'neutral' && `flos-list-item--${tone}`, className)}
    >
      {onClick ? (
        <button type="button" className="flos-list-item__button" onClick={onClick}>
          {body}
        </button>
      ) : (
        <div className="flos-list-item__inner">{body}</div>
      )}
      {trailing ? <span className="flos-list-item__trailing">{trailing}</span> : null}
    </li>
  );
}
